/**
 * Copy of the eval route shapes in obsessless-api:
 *   src/routes/evals/evalRunTypes.ts, adminListEvalSuites.ts, runEvalRequest.ts, adminJudgePrompts.ts
 *   src/evals/types.ts, summary.ts, models.ts
 * Keep in sync when those change. Times are epoch ms.
 */

export type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

/** An arm as a run request sends it. The API makes its ID from the setup. */
export interface RunEvalArm {
  model: string;
  effort?: Effort;
  promptOverride?: string;
}

/** USD per million tokens (Bedrock on-demand, global inference). */
export interface ModelPricing {
  input: number;
  output: number;
  cacheWrite: number;
  cacheRead: number;
}

export interface EvalModel {
  id: string;
  label: string;
  efforts: Effort[];
  pricing: ModelPricing;
  /** The judge only: listed for its label and price, never offered for an arm. */
  judgeOnly?: boolean;
}

/** What one version of a case holds. */
export interface EvalCaseContent {
  description: string;
  tags: string[];
  /** The suite's own input shape. */
  input: unknown;
  /** The suite's own expectations shape. */
  expect: unknown;
}

/**
 * Set on a version a person archived: a rejected proposal or a mistake. It stays, so old
 * runs keep their links, but it cannot be made current or chosen for a run.
 */
export interface VersionArchive {
  archivedAt: number;
  archivedBy: string;
  /** Can be empty. Claude reads it before it proposes again. */
  reason: string;
}

/** One saved version of a case. Never changed after it is saved, apart from `archived`. */
export interface EvalCaseVersion extends EvalCaseContent {
  versionId: string;
  number: number;
  hash: string;
  note: string;
  createdAt: number;
  createdBy: string;
  /** Set when Claude proposed it. */
  tokenName?: string;
  archived?: VersionArchive;
}

/** A case as a run uses it: the content of one version. */
export interface EvalCaseSnapshot extends EvalCaseContent {
  id: string;
  versionId: string;
  number: number;
  hash: string;
}

/**
 * `active`: new runs use it. `proposed`: no version is current yet, as for a case Claude
 * proposed. `archived`: new runs skip it; old runs keep their copy.
 */
export type EvalCaseStatus = 'active' | 'proposed' | 'archived';

export interface EvalCaseInfo {
  suiteId: string;
  caseId: string;
  status: EvalCaseStatus;
  /** Null until a person makes a version current. */
  current: EvalCaseSnapshot | null;
  latestNumber: number;
  /** Versions that wait for review: not archived, and newer than the current one (any, when there is none). */
  pendingVersions: number;
  archived: boolean;
  updatedAt: number;
  updatedBy: string;
}

export interface EvalCaseDetail extends EvalCaseInfo {
  /** Newest first. */
  versions: EvalCaseVersion[];
}

export interface EvalSuiteInfo {
  id: string;
  promptKey: string;
  productionModel: string;
  promptHash: string;
  /** The current version of each active case: what a new run uses. */
  cases: EvalCaseSnapshot[];
  /** Each judge with its versions, to choose one for a run. */
  judgePrompts: JudgePromptSummary[];
  /** Lists the case form offers, by name, e.g. `tools` for ollie-chat. */
  caseOptions: Record<string, string[]>;
}

/** A judge and its versions without the texts. */
export interface JudgePromptSummary extends Omit<JudgePromptInfo, 'versions'> {
  versions: Omit<JudgePromptVersion, 'text'>[];
}

/** The production prompt text is fetched only when a draft is started. */
export interface EvalPromptResponse {
  prompt: string;
  promptHash: string;
}

export interface EvalLimits {
  maxArms: number;
  maxTrials: number;
  maxTrialsPerRun: number;
  maxPromptOverrideChars: number;
}

export interface ListEvalSuitesResponse {
  suites: EvalSuiteInfo[];
  models: EvalModel[];
  judgeModel: string;
  limits: EvalLimits;
}

export interface RunEvalRequest {
  suiteId: string;
  arms: RunEvalArm[];
  trials: number;
  caseIds?: string[];
  /** Judge name to version ID, for a version that is not current. Other judges use the current one. */
  judgePromptVersions?: Record<string, string>;
}

export interface AiCallMeta {
  model: string;
  stopReason: string | null;
  usage: {
    inputTokens: number;
    outputTokens: number;
    cacheReadInputTokens: number;
    cacheCreationInputTokens: number;
  };
}

export type Verdict = 'pass' | 'fail' | 'unknown';

export interface CheckResult {
  id: string;
  kind: 'code' | 'judge';
  verdict: Verdict;
  detail?: string;
}

export interface TargetOutput {
  text: string;
  toolCalls: { id: string; name: string; arguments: string }[];
  meta?: AiCallMeta;
  durationMs: number;
}

export interface TrialResult {
  caseId: string;
  armId: string;
  trial: number;
  status: 'ok' | 'error' | 'skipped';
  output?: TargetOutput;
  error?: string;
  checks: CheckResult[];
  judgeCalls: AiCallMeta[];
  passed: boolean;
}

export interface TokenTotals {
  input: number;
  output: number;
  cacheRead: number;
  cacheCreation: number;
}

export interface ArmSummary {
  armId: string;
  trials: number;
  passed: number;
  errors: number;
  skipped: number;
  refusals: number;
  passRate: number | null;
  checks: Record<string, { pass: number; fail: number; unknown: number }>;
  targetTokens: TokenTotals;
  judgeTokens: TokenTotals;
  medianDurationMs: number | null;
}

export interface CellSummary {
  caseId: string;
  armId: string;
  trials: number;
  passed: number;
}

export interface RunSummary {
  arms: ArmSummary[];
  cells: CellSummary[];
}

export interface RunEvalArmInfo extends Omit<RunEvalArm, 'promptOverride'> {
  /** Made from the setup: model, effort and 6 characters of the prompt hash, e.g. `haiku-5.5-low-a1b2c3`. */
  id: string;
  hasPromptOverride: boolean;
  promptHash: string;
}

/** `queued` and `running` are active; the rest are final. */
export type EvalRunStatus = 'queued' | 'running' | 'completed' | 'deadline' | 'cancelled' | 'failed';

export const isActiveRun = (status: EvalRunStatus): boolean => status === 'queued' || status === 'running';

export interface EvalRunRecord {
  suiteId: string;
  promptKey: string;
  judgeModel: string;
  arms: RunEvalArmInfo[];
  trials: number;
  caseIds: string[];
  /** Cases x arms x trials. */
  total: number;
  startedAt: number;
  /** 0 until the run ends. */
  durationMs: number;
  status: EvalRunStatus;
  /** Why a run failed. */
  error?: string;
  /** While the run is active, the totals so far. */
  summary: RunSummary;
  createdBy: string;
  /** Set when Claude started the run through the evals MCP server. */
  tokenName?: string;
  /** The judge prompt versions the run uses, fixed when it is queued. */
  judgePrompts: JudgePromptRef[];
  /** Set on a re-grade: a run that graded another run's outputs again, with no model calls. */
  regradeOf?: RegradeSource;
  /** On a re-grade that ended: the totals of its report. */
  regradeSummary?: RegradeSummary;
}

export interface RegradeSummary {
  compared: number;
  flaggedTotal: number;
  fixed: number;
  same: number;
  unknown: number;
  otherChanges: number;
}

export type RegradeScope = 'all' | 'flagged';

export interface RegradeSource {
  runId: string;
  scope: RegradeScope;
  resultIds: string[];
}

export interface RegradeCheckChange {
  resultId: string;
  caseId: string;
  armId: string;
  trial: number;
  checkId: string;
  before: Verdict;
  /** `null` when the re-grade has no such check. */
  after: Verdict | null;
  detail?: string;
  flagNote?: string;
}

/** A re-grade compared with its source run (obsessless-api src/routes/evals/regradeReport.ts). */
export interface RegradeReport {
  sourceRunId: string;
  sourceStartedAt: number;
  /** The judge prompt versions the source run used. */
  sourceJudgePrompts: JudgePromptRef[];
  compared: number;
  passRate: { before: number | null; after: number | null };
  flagged: { total: number; fixed: number; same: number; unknown: number; checks: RegradeCheckChange[] };
  otherChanges: RegradeCheckChange[];
}

/** One saved text of a judge prompt. Never changed after it is saved. */
export interface JudgePromptVersion {
  versionId: string;
  number: number;
  text: string;
  hash: string;
  note: string;
  createdAt: number;
  /** UID of the admin who saved it. `seed` on the summary judge's v1, which came from the suite code. */
  createdBy: string;
  /** Set when Claude proposed it. */
  tokenName?: string;
  archived?: VersionArchive;
}

export interface JudgePromptRef {
  judgeName: string;
  versionId: string;
  number: number;
  hash: string;
  /** The version was current when the run was queued. */
  wasCurrent: boolean;
}

/** One judge of one suite, with every version, newest first. */
export interface JudgePromptInfo {
  suiteId: string;
  judgeName: string;
  /** `null` while the judge has no versions, and so no prompt: runs of its suite cannot start. */
  currentVersionId: string | null;
  versions: JudgePromptVersion[];
}

export interface EvalReview {
  decision: 'go' | 'no-go';
  note: string;
  reviewedBy: string;
  reviewedAt: number;
}

export interface JudgeFlag {
  checkIds: string[];
  note: string;
  flaggedBy: string;
  flaggedAt: number;
}

export interface EvalRunItem extends EvalRunRecord {
  runId: string;
  resultCount: number;
  /** Someone asked the running run to stop; it stops within a few seconds. */
  cancelRequested: boolean;
  /** Suggestions from Claude that wait for a human. */
  pendingSuggestions: number;
  review: EvalReview | null;
}

/** One page of runs, newest first. */
export interface EvalRunPage {
  runs: EvalRunItem[];
  /** Pass as `before` for the next page. `null` on the last page. */
  nextBefore: number | null;
}

/**
 * What waits for a human: suggestions from Claude, and cases and judge prompts with a
 * version that is not current.
 */
export interface EvalReviewQueue {
  runs: { runId: string; suiteId: string; startedAt: number; pendingSuggestions: number }[];
  cases: { suiteId: string; caseId: string; status: EvalCaseStatus; currentNumber: number | null; pendingVersions: number }[];
  judgePrompts: { suiteId: string; judgeName: string; currentNumber: number | null; pendingVersions: number }[];
}

export interface StoredTrialResult extends TrialResult {
  resultId: string;
  judgeFlag: JudgeFlag | null;
}

export interface PromptOverride {
  armId: string;
  promptHash: string;
  prompt: string;
}

/** A judge flag or review Claude proposed through the evals MCP server. Nothing changes until a human accepts it. */
interface EvalSuggestionBase {
  suggestionId: string;
  /** Claude's reason. */
  note: string;
  /** UID of the API token's owner. */
  suggestedBy: string;
  tokenName: string;
  createdAt: number;
  status: 'pending' | 'accepted' | 'rejected';
  decidedBy: string | null;
  decidedAt: number | null;
  rejectReason: string | null;
}

export interface JudgeFlagSuggestion extends EvalSuggestionBase {
  kind: 'judge-flag';
  resultId: string;
  checkIds: string[];
}

export interface ReviewSuggestion extends EvalSuggestionBase {
  kind: 'review';
  decision: EvalReview['decision'];
}

export type EvalSuggestion = JudgeFlagSuggestion | ReviewSuggestion;

/** A decided suggestion, with the flag or review that accepting it made. */
export interface DecidedSuggestion {
  suggestion: EvalSuggestion;
  judgeFlag: JudgeFlag | null;
  review: EvalReview | null;
}

export interface EvalRunDetail {
  run: EvalRunItem;
  results: StoredTrialResult[];
  prompts: PromptOverride[];
  /** The cases as the run used them, in run order. */
  cases: EvalCaseSnapshot[];
  /** Newest first, decided ones included. */
  suggestions: EvalSuggestion[];
  /** The re-grades of this run, newest first. */
  regrades: EvalRunItem[];
}
